import { camelCase, kebabCase, compact, last, isNil } from 'lodash'
import { DateTime } from 'luxon'
import {
  DataSection,
  AccountBalanceReport,
  ReportSection,
  Align,
  AccountBalances,
} from '../../../types'
import { ConfigSection } from '../../../../../shared/types'
import { TableConnected } from 'elements'
import { getAnomalyDataSections } from '../../../utils/anomalies'
import { formatAnomalyForExport } from '../../../export/formatters'
import { findObjectsWithProperty } from '../../../../../utils/object'

const { LedgerCell, PercentageCell, ExpandSubRowsHeader } = TableConnected as any

type GetDataSectionsProps = {
  report: AccountBalanceReport
  configSections?: ConfigSection[]
  context: 'table' | 'export'
  history: any
  location: any
}

type DepthSection = {
  name: string
  sectionDepth: number
}

// report menu width - page content padding
const TABLE_STICKY_LEFT = '268px'

// loop through account sections and assign depth
const getSectionDepths = (data: AccountBalances[]): DepthSection[] => {
  const accountSections: DepthSection[] = []

  let sectionDepth = 0

  data.forEach((row, idx) => {
    const previousRow = data[idx - 1]

    if (previousRow?.accountSection) {
      sectionDepth++
    }

    if (previousRow?.summary && sectionDepth > 0) {
      sectionDepth = sectionDepth - 1
    }

    if (row.accountSection) {
      accountSections.push({ name: row.accountSection, sectionDepth })
    }
  })

  return accountSections
}

// loop backwards through the array of account sections to find the last one with the correct depth (one higher)
const findPreviousParent = (currentParentSection: string, accountSections: DepthSection[]) => {
  const currentParent = accountSections.find((section) => section.name === currentParentSection)
  const sectionIdx = accountSections.indexOf(currentParent!)

  let previousParent: string | null = null
  let idx = sectionIdx - 1

  while (!previousParent && idx >= 0) {
    if (accountSections[idx]?.sectionDepth === currentParent!.sectionDepth - 1) {
      previousParent = accountSections[idx]?.name
    }

    idx = idx - 1
  }

  return previousParent
}

const getBalanceReportData = (report: AccountBalanceReport) => {
  const accountSections = getSectionDepths(report.data)

  const finalData: any[] = []
  let parentSection: string | null = null

  report.data!.forEach((row, idx) => {
    const previousRow = report.data![idx - 1]

    if (previousRow?.accountSection) {
      parentSection = previousRow.accountSection
    }

    if (previousRow?.summary) {
      parentSection = findPreviousParent(parentSection!, accountSections)
    }

    // populate row
    const finalRow: any = { ...row }

    // populate section header row
    if (row.accountSection) {
      finalRow.subRows = []
    } else {
      // populate data row
      finalRow.account = row.accountName

      for (const month of row.balanceByMonth!) {
        finalRow[`${month.monthName}-current`] = month.currentBalance
        finalRow[`${month.monthName}-previous`] = month.previousBalance
        finalRow[`${month.monthName}-balanceChange`] = month.balanceChange
        finalRow[`${month.monthName}-percentChange`] = month.ratioChange
      }
    }

    // determine where to put row
    if (!parentSection) {
      finalData.push(finalRow)
      return
    }

    // locate the account section with the given name. In case of accounts or sub-accounts with duplicate names,
    // take the last one to get the correct parent
    const parentObj = last(
      findObjectsWithProperty({
        object: finalData,
        key: 'accountSection',
        value: parentSection,
      }),
    )

    if (parentObj) {
      parentObj.subRows.push(finalRow)
    }
  })

  return finalData
}

type GetTableOptionsProps = {
  report: AccountBalanceReport
  configSections?: ConfigSection[]
  history: any
  location: any
}

function getBalanceReportTableOptions(props: GetTableOptionsProps) {
  const { report, configSections, history, location } = props

  const config = configSections?.find((section) => section.name === camelCase(report.reportName))
    ?.config

  return {
    startExpanded: true,
    cellProps: (cell: any) => {
      const { row, column } = cell
      const parentMonth = column.parent?.Header

      const props: any = {}

      if (!parentMonth?.length) {
        return props
      }

      // bold balance and change cells if over threshold
      if (config) {
        const { balanceChange, percentChange } = config

        const monthBalanceChange = row.original[`${parentMonth}-balanceChange`]
        const monthPercentChange = row.original[`${parentMonth}-percentChange`]

        if (
          (balanceChange && Math.abs(monthBalanceChange) >= balanceChange) ||
          (percentChange && Math.abs(monthPercentChange) >= percentChange)
        ) {
          props.fontWeight = 'bold'
        }
      }

      const month = DateTime.fromFormat(parentMonth, 'MMM yyyy')
      const accountId = row.original.accountId

      if (month?.isValid && accountId && !isNil(cell.value)) {
        props.onClick = () => {
          const startDate = month.startOf('month').toISODate()
          const endDate = month.endOf('month').toISODate()

          const newLocation = `${location.pathname}/details?startDate=${startDate}&endDate=${endDate}&accountId=${accountId}`

          history.push(newLocation)
        }
      }

      return props
    },
    tableProps: { overflow: null },
  }
}

function getBalanceReportDataSections(props: GetDataSectionsProps): DataSection[] {
  const { report, context, configSections, history, location } = props
  const key = camelCase(report.reportName)

  const data = getBalanceReportData(report)

  const sampleMonths = report.data.find((account) => account.balanceByMonth)!.balanceByMonth!

  if (context === 'table') {
    return [
      {
        key,
        table: true,
        data,
        columns: [
          {
            Header: '',
            accessor: 'accountParentColumn',
            width: '350px',
            disableSortBy: true,
            sticky: true,
            stickyPosition: { left: TABLE_STICKY_LEFT },
            columns: [
              {
                id: 'expander',
                accessor: (row: any) => row.account ?? row.accountSection,
                Header: 'Account',
                Cell: ExpandSubRowsHeader,
                sticky: true,
                stickyPosition: { left: TABLE_STICKY_LEFT },
                disableSortBy: true,
                style: {
                  headCell: { borderRight: 'solid 1px' },
                  dataCell: { borderRight: 'solid 1px' },
                },
              },
            ],
            style: { headCell: { borderBottom: 'solid 1px' } },
          },
          ...sampleMonths.map((month: any) => ({
            Header: month.monthName,
            accessor: '',
            align: 'center' as Align,
            disableSortBy: true,
            columns: [
              {
                Header: 'Current',
                accessor: `${month.monthName}-current`,
                disableSortBy: true,
                Cell: LedgerCell,
              },
              {
                Header: month.previousMonthName,
                accessor: `${month.monthName}-previous`,
                disableSortBy: true,
                Cell: LedgerCell,
              },
              {
                Header: '% Change',
                accessor: `${month.monthName}-percentChange`,
                disableSortBy: true,
                Cell: PercentageCell,
                style: { headCell: { borderRight: 'solid 1px' } },
              },
            ],
            width: '400px',
            style: { headCell: { borderBottom: 'solid 1px' } },
          })),
        ],
        tableOptions: getBalanceReportTableOptions({ report, configSections, history, location }),
      },
    ]
  }

  //leave out export data for now
  return []
}

type GetDetailReportSectionProps = {
  reports: any[]
  configSections?: ConfigSection[]
  history: any
  location: any
  reportUrl?: string
  reportEndDate: string
}
export default function getDetailReportSections(
  props: GetDetailReportSectionProps,
): ReportSection[] {
  const { reports, configSections, history, reportUrl, reportEndDate, location } = props

  return reports
    .filter((report) => report.data?.length || report.transactions?.length)
    .map((report) => {
      //account balance reports
      if (report.reportName) {
        return {
          label: report.reportName,
          value: camelCase(report.reportName),
          route: kebabCase(report.reportName),
          kind: 'report',
          onSelect() {
            history.push(`${reportUrl}/${this.route}`)
          },
          dataSections: getBalanceReportDataSections({
            report,
            context: 'table',
            configSections,
            history,
            location,
          }),
          viewOptions: { disableViewModes: true },
          exportData: [],
          subReportSections: [
            {
              label: 'Details',
              value: 'details',
              kind: 'report',
              route: 'details',
              // data section will be added later based on query results
              dataSections: [],
              exportData: [],
              viewOptions: { disableViewModes: true },
            },
          ],
        }
      }

      //ar and ap aging
      return {
        label: report.anomalyName,
        value: camelCase(report.anomalyName),
        route: kebabCase(report.anomalyName),
        kind: 'report',
        onSelect() {
          history.push(`${reportUrl}/${this.route}`)
        },
        dataSections: getAnomalyDataSections({
          anomaly: report,
          context: 'table',
          configSections,
          reportEndDate,
        }),
        exportData: compact([formatAnomalyForExport(report, reportEndDate, configSections)]),
      }
    })
}
